import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAccount, useSwitchChain } from 'wagmi';
import { supabase } from '../utils/supabase';
import { useTopFavoritedTokensByChain, useTokenFavoritesByChain, FavoriteChain } from '../hooks/useUsers';
import { useTokens } from '../hooks/useTokens';
import { ZNSRegistrationModal } from '../components/shared/ZNSRegistrationModal';
import TradeModal from '../components/shared/modals/TradeModal';
import SolanaTradeModal from '../components/shared/modals/SolanaTradeModal';
import { SolanaWalletProvider } from '../components/SolanaWalletProvider';
import { getLastFourChars, formatMarketCap } from '../utils/address';
import { getRelayTokenPrice } from '../hooks/useRelayPrice';
import { useRealtimePrices, formatPriceChange, getPriceChangeColor } from '../hooks/useRealtimePrices';
import { ChevronRight, Plus, Globe, Star, Clock, Sparkles, ExternalLink, Twitter, Send, Copy, Heart } from 'lucide-react';
import { sanitizeUrl } from '../utils/sanitizeUrl';
import { ChainFilter } from '../components/shared/ChainToggle';

// ============================================
// BEHOLDER-STYLE EXPLORE PAGE
// ============================================

const PageContainer = styled.div`
  padding: 24px;
  background: transparent;
  min-height: calc(100vh - 64px - 56px);
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

// ============ CHAIN TOGGLE SECTION ============
const ChainToggleSection = styled.div`
  width: 100%;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
  }
`;

const ChainToggleContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 4px;
  backdrop-filter: blur(10px);
  width: 100%;
`;

const ChainButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 8px;
  border: none;
  background: ${props => props.$active 
    ? 'linear-gradient(135deg, #a855f7 0%, #9333ea 100%)' 
    : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$active 
      ? 'linear-gradient(135deg, #a855f7 0%, #9333ea 100%)' 
      : 'rgba(255, 255, 255, 0.1)'};
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  @media (max-width: 768px) {
    padding: 6px;
  }
`;

const ChainLogo = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: contain;
  
  @media (max-width: 768px) {
    width: 16px;
    height: 16px;
  }
`;

// Chain configuration
const CHAIN_CONFIG = [
  { value: 'ink' as ChainFilter, label: 'Ink', logo: '/landing-assets/Ink.png' },
  { value: 'eth' as ChainFilter, label: 'Ethereum', logo: '/landing-assets/ETH_logo.png' },
  { value: 'wlfi' as ChainFilter, label: 'USD1', logo: '/USD1_1000x1000_transparent.png' },
  { value: 'mon' as ChainFilter, label: 'Monad', logo: '/Monad.png' },
  { value: 'sol' as ChainFilter, label: 'Solana', logo: '/landing-assets/Solana.png' },
];

// Chain ID mapping for wallet switching (0 = non-EVM chain like Solana)
const CHAIN_ID_MAP: Record<ChainFilter, number> = {
  ink: 57073,
  eth: 1,
  wlfi: 1, // WLFI uses Ethereum mainnet
  mon: 143, // Monad testnet
  sol: 0,  // Solana is not EVM - handle separately
};

// Price chain ID mapping (for token_prices table lookups via Relay API)
const PRICE_CHAIN_ID_MAP: Record<ChainFilter, number> = {
  ink: 57073,
  eth: 1,
  wlfi: 1,
  mon: 143,
  sol: 792703809, // Relay API Solana chain ID
};

// Supabase table mapping
const TABLE_MAP: Record<ChainFilter, string> = {
  ink: 'tokens',
  eth: 'tokens_eth',
  wlfi: 'tokens_usd1',
  mon: 'tokens_monad',
  sol: 'tokens_sol',
};

// ============ HERO SECTION ============
const HeroSection = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 20px;
  margin-bottom: 32px;
  
  @media (max-width: 1100px) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const HeroCard = styled.div<{ $featured?: boolean }>`
  background: rgba(20, 20, 25, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;
  cursor: pointer;
  height: 470px;
  display: flex;
  flex-direction: column;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.1);
  }
  
  @media (max-width: 768px) {
    height: auto;
    min-height: 200px;
  }
`;

const HeroCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const HeroCardLogo = styled.img<{ $large?: boolean }>`
  width: ${props => props.$large ? '48px' : '36px'};
  height: ${props => props.$large ? '48px' : '36px'};
  border-radius: 50%;
  object-fit: cover;
  background: #000000;
`;

const HeroCardTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
`;

const HeroCardSubtitle = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 2px;
`;

const HeroCardStat = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #10b981;
  margin-bottom: 8px;
`;

const HeroCardDescription = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.5;
  margin-bottom: 12px;
`;

const HeroCardButton = styled.button`
  padding: 10px 24px;
  background: linear-gradient(135deg, #a855f7, #7c3aed);
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(135deg, #9333ea, #6d28d9);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px #333333;
  }
`;

const HeroCardBadge = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.6);
  
  svg {
    width: 10px;
    height: 10px;
  }
`;

// Community Favorite Card - Beholder-Style Layout
const CommunityFavoriteContainer = styled.div`
  display: flex;
  gap: 24px;
  height: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const CommunityFavoriteLogoWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CommunityFavoriteLogo = styled.img`
  width: 220px;
  height: 220px;
  border-radius: 50%;
  object-fit: cover;
  background: #000000;
  
  @media (max-width: 768px) {
    width: 150px;
    height: 150px;
  }
`;

const CommunityFavoriteContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding-top: 24px;
`;

const CommunityFavoriteTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const CommunityFavoriteNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const CommunityFavoriteName = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const ProtofireLogo = styled.img`
  width: 22px;
  height: 22px;
  border-radius: 4px;
  object-fit: contain;
`;

const CommunityFavoriteTradeButton = styled.button`
  padding: 8px 20px;
  background: linear-gradient(135deg, #a855f7, #7c3aed);
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.5px;
  
  &:hover {
    background: linear-gradient(135deg, #9333ea, #6d28d9);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px #333333;
  }
`;

const CommunityFavoriteMarketCap = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #10b981;
  white-space: nowrap;
  padding: 4px 0;
`;

const CommunityFavoriteSymbolRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const CommunityFavoriteSymbol = styled.span`
  font-size: 14px;
  color: #f97316;
  font-weight: 500;
`;

const CommunityFavoriteAddress = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
`;

const CommunityFavoriteDevRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const DevIcon = styled.img`
  width: 18px;
  height: 18px;
  border-radius: 4px;
  object-fit: contain;
`;

const DevAddress = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
`;

const CommunityFavoriteDescription = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.6;
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  flex: 1;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
`;

const CommunityFavoriteViewMore = styled.a`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  margin-bottom: 12px;
  display: inline-block;
  
  &:hover {
    color: rgba(255, 255, 255, 0.7);
  }
`;

const CommunityFavoriteSocials = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: auto;
  flex-wrap: wrap;
`;

const SocialIcon = styled.a`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.9);
    border-color: rgba(255, 255, 255, 0.15);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ZNSCard = styled(HeroCard)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px;
`;

const ZNSLogo = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: contain;
  margin-bottom: 32px;
  
  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
    margin-bottom: 24px;
  }
`;

const ZNSTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 6px 0;
`;

const ZNSSubtitle = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 16px;
`;

const ZNSDescription = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.5;
  margin-bottom: 24px;
  max-width: 280px;
`;

const CreateCard = styled(HeroCard)`
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const CreateIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  
  svg {
    width: 24px;
    height: 24px;
    color: rgba(255, 255, 255, 0.6);
  }
`;

// ============ TOKEN LIST SECTIONS ============
const ListSectionContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 32px;
  
  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ListSection = styled.div`
  background: rgba(20, 20, 25, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 485px;
  
  @media (max-width: 768px) {
    height: auto;
    min-height: 300px;
  }
`;

const ListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const ListTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  
  svg {
    width: 16px;
    height: 16px;
    color: rgba(255, 255, 255, 0.5);
  }
`;

const ListExpandButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ListContent = styled.div<{ $expanded?: boolean }>`
  flex: 1;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
`;

const TokenRow = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 16px;
  gap: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  
  &:hover {
    background: #000000;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const TokenLogo = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  object-fit: cover;
  background: #000000;
  border: 1px solid #333333;
  flex-shrink: 0;
`;

const TokenInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TokenNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TokenName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TokenDexLogo = styled.img`
  width: 14px;
  height: 14px;
  border-radius: 3px;
  object-fit: contain;
`;

const TokenSymbol = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
`;

const TokenStats = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
`;

const TokenMarketCap = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #10b981;
`;

const TokenPrice = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
`;

const PriceChange = styled.div<{ $positive?: boolean }>`
  font-size: 10px;
  font-weight: 500;
  color: ${props => props.$positive ? '#10b981' : '#ef4444'};
`;

const TokenActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const TokenTradeButton = styled.button`
  padding: 5px 10px;
  background: linear-gradient(135deg, #a855f7, #7c3aed);
  border: none;
  border-radius: 6px;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(135deg, #9333ea, #6d28d9);
    transform: translateY(-1px);
  }
`;

const TokenFavoriteButton = styled.button<{ $isFavorited: boolean }>`
  width: 28px;
  height: 28px;
  padding: 0;
  background: ${props => props.$isFavorited ? '#000000' : 'transparent'};
  border: 1px solid ${props => props.$isFavorited ? '#333333' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 6px;
  color: ${props => props.$isFavorited ? '#a855f7' : 'rgba(255, 255, 255, 0.4)'};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #000000;
    border-color: #333333;
    color: #a855f7;
  }
  
  svg {
    width: 14px;
    height: 14px;
    fill: ${props => props.$isFavorited ? '#a855f7' : 'none'};
  }
`;

const TokenFavorites = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #fbbf24;
  
  svg {
    width: 12px;
    height: 12px;
    fill: #fbbf24;
  }
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
`;

// ============ SCROLLING TOKEN FEED ============
const ScrollingFeedContainer = styled.div`
  width: 100%;
  overflow: hidden;
  background: rgba(20, 20, 25, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 16px 0;
  margin-top: 24px;
`;

const ScrollingFeedTrack = styled.div`
  display: flex;
  animation: scroll 60s linear infinite;
  width: fit-content;
  
  &:hover {
    animation-play-state: paused;
  }
  
  @keyframes scroll {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-50%);
    }
  }
`;

const ScrollingTokenCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  margin: 0 8px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  
  &:hover {
    background: #000000;
    border-color: #333333;
    transform: translateY(-2px);
  }
`;

const ScrollingTokenLogo = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  background: #000000;
`;

const ScrollingTokenInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ScrollingTokenSymbol = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
`;

const ScrollingTokenCategory = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: capitalize;
`;

const ScrollingTokenMarketCap = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #10b981;
  margin-left: auto;
`;

// ============ HELPER FUNCTIONS ============
const ensureHttpsUrl = (url: string | null | undefined): string => {
  if (!url) return '/token-placeholder.png';
  if (url.startsWith('Qm') && url.length === 46) {
    return `https://ipfs.io/ipfs/${url}`;
  }
  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }
  return url;
};

// ============ COMPONENT ============
const ExplorePageV2: React.FC = () => {
  const navigate = useNavigate();
  const { address, isConnected } = useAccount();
  const { switchChain } = useSwitchChain();
  
  // Chain filter state
  const [selectedChain, setSelectedChain] = useState<ChainFilter>('ink');
  
  // Modal states
  const [znsModalOpen, setZnsModalOpen] = useState(false);
  const [tradeModalOpen, setTradeModalOpen] = useState(false);
  const [solanaTradeModalOpen, setSolanaTradeModalOpen] = useState(false);
  const [selectedToken, setSelectedToken] = useState<any>(null);
  
  // Expansion states
  const [recentlyDeployedExpanded, setRecentlyDeployedExpanded] = useState(false);
  const [featuredExpanded, setFeaturedExpanded] = useState(false);
  const [favoritesExpanded, setFavoritesExpanded] = useState(false);
  
  // Data states
  const [recentlyDeployed, setRecentlyDeployed] = useState<any[]>([]);
  const [featuredTokens, setFeaturedTokens] = useState<any[]>([]);
  const [communityFavorite, setCommunityFavorite] = useState<any>(null);
  const [communityFavoritePrice, setCommunityFavoritePrice] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [scrollingTokens, setScrollingTokens] = useState<any[]>([]);
  const [brokenImages, setBrokenImages] = useState<Set<string>>(new Set());
  
  // Handle chain change - switch wallet network
  const handleChainChange = (chain: ChainFilter) => {
    setSelectedChain(chain);
    
    // Switch wallet network if connected
    if (isConnected && switchChain) {
      const targetChainId = CHAIN_ID_MAP[chain];
      console.log(`[ExplorePageV2] Switching to chain ${chain} (ID: ${targetChainId})`);
      try {
        switchChain({ chainId: targetChainId });
      } catch (error) {
        console.error('[ExplorePageV2] Failed to switch chain:', error);
      }
    }
  };
  
  // Get top favorited tokens - chain aware
  const { topTokens: communityFavorites, loading: favoritesLoading } = useTopFavoritedTokensByChain(selectedChain as FavoriteChain, 20);
  
  // Favorites functionality - chain aware
  const { toggleFavorite, userFavorites } = useTokenFavoritesByChain(selectedChain as FavoriteChain, address);
  const [localFavorites, setLocalFavorites] = useState<Set<string>>(new Set());
  
  // Sync local favorites with user favorites from hook
  useEffect(() => {
    setLocalFavorites(userFavorites);
  }, [userFavorites]);
  
  // Real-time prices from Supabase cache (instant loading!)
  // Pass the price chain ID for the selected chain
  const priceChainId = PRICE_CHAIN_ID_MAP[selectedChain];
  const { getPriceForToken, get24hChangeForToken, loading: pricesLoading } = useRealtimePrices(priceChainId);
  
  // Helper to get DEX logo
  const getNetworkLogo = (dex?: string) => {
    if (dex === 'InkySwap') return '/InkySwap.png';
    return '/Protofire.png';
  };
  
  // Handle favorite toggle
  const handleToggleFavorite = useCallback(async (tokenId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isConnected || !address) return;
    
    const isCurrentlyFavorited = localFavorites.has(tokenId);
    
    // Optimistic update
    setLocalFavorites(prev => {
      const newSet = new Set(prev);
      if (isCurrentlyFavorited) {
        newSet.delete(tokenId);
      } else {
        newSet.add(tokenId);
      }
      return newSet;
    });
    
    // Sync with database
    await toggleFavorite(tokenId, isCurrentlyFavorited);
  }, [isConnected, address, localFavorites, toggleFavorite]);
  
  // Format price with subscript for small values
  const formatPriceDisplay = (price: number | null): React.ReactNode => {
    if (!price) return 'N/A';
    if (price >= 0.01) return `$${price.toFixed(4)}`;
    
    const priceStr = price.toFixed(20);
    const match = priceStr.match(/^0\.0*([1-9])/);
    if (match) {
      const zeros = priceStr.indexOf(match[1]) - 2;
      const significantDigits = priceStr.slice(priceStr.indexOf(match[1]), priceStr.indexOf(match[1]) + 2);
      return <>$0.0<sub>{zeros}</sub>{significantDigits}</>;
    }
    return `$${price.toFixed(6)}`;
  };
  
  // Fetch recently deployed tokens - based on selected chain
  useEffect(() => {
    const fetchRecentlyDeployed = async () => {
      try {
        const tableName = TABLE_MAP[selectedChain];
        const { data, error } = await supabase
          .from(tableName)
          .select('*')
          .order('created_at', { ascending: false })
          .limit(20);
        
        if (!error && data) {
          setRecentlyDeployed(data);
        }
      } catch (err) {
        console.error('Error fetching recently deployed:', err);
      }
    };
    
    fetchRecentlyDeployed();
  }, [selectedChain]);
  
  // Fetch random tokens for scrolling feed - based on selected chain
  useEffect(() => {
    const fetchScrollingTokens = async () => {
      try {
        const tableName = TABLE_MAP[selectedChain];
        // Fetch tokens that have logos and randomize them
        const { data, error } = await supabase
          .from(tableName)
          .select('id, name, symbol, logo_url, category, contract_address')
          .not('logo_url', 'is', null)
          .neq('logo_url', '')
          .limit(100);
        
        if (!error && data && data.length > 0) {
          // Filter to only tokens with valid-looking logo URLs
          const validLogoTokens = data.filter(token => {
            const url = token.logo_url;
            if (!url) return false;
            // Accept URLs that start with http/https, or are IPFS hashes, or are from our storage
            return (
              url.startsWith('http://') || 
              url.startsWith('https://') || 
              url.startsWith('Qm') || // IPFS hash
              url.includes('supabase') ||
              url.includes('cloudinary') ||
              url.includes('imgur') ||
              url.includes('ibb.co')
            );
          });
          
          // Shuffle the tokens randomly
          const shuffled = [...validLogoTokens].sort(() => Math.random() - 0.5).slice(0, 50);
          setScrollingTokens(shuffled);
          setBrokenImages(new Set()); // Reset broken images when chain changes
        }
      } catch (err) {
        console.error('Error fetching scrolling tokens:', err);
      }
    };
    
    fetchScrollingTokens();
  }, [selectedChain]);
  
  // Fetch featured tokens - based on selected chain
  useEffect(() => {
    const fetchFeaturedTokens = async () => {
      try {
        const tableName = TABLE_MAP[selectedChain];
        // First try to fetch from tokens_featured table (only for Ink chain)
        if (selectedChain === 'ink') {
          const { data, error } = await supabase
            .from('tokens_featured')
            .select('*')
            .order('display_order', { ascending: true })
            .limit(10);
          
          if (!error && data && data.length > 0) {
            setFeaturedTokens(data);
            setLoading(false);
            return;
          }
        }
        
        // Fallback: use most recent tokens as featured
        // Note: Solana table doesn't have market_cap_usd column, so use created_at
        const orderColumn = selectedChain === 'sol' ? 'created_at' : 'created_at'; // Use created_at for all for consistency
        const { data: fallbackData, error: fallbackError } = await supabase
          .from(tableName)
          .select('*')
          .order(orderColumn, { ascending: false })
          .limit(10);
        
        if (!fallbackError && fallbackData) {
          setFeaturedTokens(fallbackData);
        }
      } catch (err) {
        console.error('Error fetching featured tokens:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFeaturedTokens();
  }, [selectedChain]);
  
  // Set community favorite (top favorited token)
  useEffect(() => {
    if (communityFavorites && communityFavorites.length > 0) {
      setCommunityFavorite(communityFavorites[0]);
    }
  }, [communityFavorites]);
  
  // Fetch price for community favorite token
  useEffect(() => {
    const fetchPrice = async () => {
      if (communityFavorite?.contract_address) {
        const price = await getRelayTokenPrice(communityFavorite.contract_address);
        setCommunityFavoritePrice(price);
        console.log(`[ExploreV2] Community favorite price: $${price}`);
      }
    };
    fetchPrice();
  }, [communityFavorite?.contract_address]);
  
  const handleTokenClick = (token: any) => {
    const isSolana = selectedChain === 'sol';
    
    setSelectedToken({
      address: token.contract_address || token.mint_address || '',
      symbol: token.symbol,
      name: token.name,
      chainId: CHAIN_ID_MAP[selectedChain],
      logo: ensureHttpsUrl(token.logo_url),
      decimals: isSolana ? 9 : 18,
      poolAddress: token.pool_address || token.whirlpool_address, // For Solana Orca pools
      // Cached pool data for Solana (reduces RPC calls)
      tokenVaultA: token.token_vault_a,
      tokenVaultB: token.token_vault_b,
      tickSpacing: token.tick_spacing,
    });
    
    if (isSolana) {
      setSolanaTradeModalOpen(true);
    } else {
      setTradeModalOpen(true);
    }
  };
  
  const displayedRecentlyDeployed = recentlyDeployedExpanded ? recentlyDeployed : recentlyDeployed.slice(0, 5);
  const displayedFeatured = featuredExpanded ? featuredTokens : featuredTokens.slice(0, 5);
  const displayedFavorites = favoritesExpanded ? communityFavorites : communityFavorites?.slice(0, 5) || [];

  return (
    <PageContainer>
      {/* ============ CHAIN TOGGLE ============ */}
      <ChainToggleSection>
        <ChainToggleContainer>
          {CHAIN_CONFIG.map((chain) => (
            <ChainButton
              key={chain.value}
              $active={selectedChain === chain.value}
              onClick={() => handleChainChange(chain.value)}
              title={`View ${chain.label} tokens`}
            >
              <ChainLogo src={chain.logo} alt={chain.label} />
            </ChainButton>
          ))}
        </ChainToggleContainer>
      </ChainToggleSection>

      {/* ============ HERO SECTION ============ */}
      <HeroSection>
        {/* Community Favorite - Large Card */}
        <HeroCard $featured>
          <HeroCardBadge>
            <Star /> Community Favorite
          </HeroCardBadge>
          {communityFavorite ? (
            <CommunityFavoriteContainer>
              <CommunityFavoriteLogoWrapper>
                <CommunityFavoriteLogo 
                  src={ensureHttpsUrl(communityFavorite.logo_url)} 
                  alt={communityFavorite.name}
                  onError={(e) => { (e.target as HTMLImageElement).src = '/token-placeholder.png'; }}
                />
              </CommunityFavoriteLogoWrapper>
              
              <CommunityFavoriteContent>
                <CommunityFavoriteTopRow>
                  <CommunityFavoriteNameRow>
                    <CommunityFavoriteName>{communityFavorite.name}</CommunityFavoriteName>
                    <ProtofireLogo 
                      src={communityFavorite.dex === 'InkySwap' ? '/InkySwap.png' : '/Protofire.png'} 
                      alt={communityFavorite.dex || 'Protofire DEX'}
                    />
                    <CommunityFavoriteTradeButton onClick={(e) => {
                      e.stopPropagation();
                      handleTokenClick(communityFavorite);
                    }}>
                      TRADE
                    </CommunityFavoriteTradeButton>
                  </CommunityFavoriteNameRow>
                  <CommunityFavoriteMarketCap>
                    MC: {communityFavoritePrice 
                      ? formatMarketCap(communityFavoritePrice) 
                      : '$0'}
                  </CommunityFavoriteMarketCap>
                </CommunityFavoriteTopRow>
                
                <CommunityFavoriteSymbolRow>
                  <CommunityFavoriteSymbol>${communityFavorite.symbol}</CommunityFavoriteSymbol>
                  <CommunityFavoriteAddress>
                    {communityFavorite.contract_address?.slice(0, 6)}...{communityFavorite.contract_address?.slice(-4)}
                  </CommunityFavoriteAddress>
                </CommunityFavoriteSymbolRow>
                
                <CommunityFavoriteDevRow>
                  <DevIcon src="/social_icons/dev_icon.png" alt="Developer" />
                  <DevAddress>
                    {communityFavorite.deployer_address?.slice(0, 6)}...{communityFavorite.deployer_address?.slice(-4)}
                  </DevAddress>
                </CommunityFavoriteDevRow>
                
                <CommunityFavoriteDescription>
                  {communityFavorite.description || 'Community favorite token on Mavrk'}
                </CommunityFavoriteDescription>
                
                <CommunityFavoriteViewMore onClick={() => navigate(`/token/${communityFavorite.contract_address}`)}>
                  View more
                </CommunityFavoriteViewMore>
                
                <CommunityFavoriteSocials>
                  <SocialIcon onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(communityFavorite.contract_address || '');
                  }}>
                    <Copy />
                  </SocialIcon>
                  {sanitizeUrl(communityFavorite.website) && (
                    <SocialIcon href={sanitizeUrl(communityFavorite.website)!} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                      <Globe />
                    </SocialIcon>
                  )}
                  {sanitizeUrl(communityFavorite.twitter) && (
                    <SocialIcon href={sanitizeUrl(communityFavorite.twitter)!} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                      <Twitter />
                    </SocialIcon>
                  )}
                  {sanitizeUrl(communityFavorite.telegram) && (
                    <SocialIcon href={sanitizeUrl(communityFavorite.telegram)!} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                      <Send />
                    </SocialIcon>
                  )}
                  <SocialIcon href={`https://explorer.inkonchain.com/token/${communityFavorite.contract_address}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 6v6l4 2" />
                    </svg>
                  </SocialIcon>
                  <SocialIcon href={`https://dexscreener.com/ink/${communityFavorite.contract_address}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </SocialIcon>
                  <SocialIcon href={`/token/${communityFavorite.contract_address}`} onClick={(e) => e.stopPropagation()}>
                    <ExternalLink />
                  </SocialIcon>
                </CommunityFavoriteSocials>
              </CommunityFavoriteContent>
            </CommunityFavoriteContainer>
          ) : (
            <EmptyState>Loading community favorite...</EmptyState>
          )}
        </HeroCard>
        
        {/* ZNS Registration Card */}
        <ZNSCard onClick={() => setZnsModalOpen(true)}>
          <ZNSLogo src="/zns_icon_purple.png" alt="ZNS" />
          <ZNSTitle>Register .ink Domain</ZNSTitle>
          <ZNSSubtitle>ZNS Name Service</ZNSSubtitle>
          <ZNSDescription>
            Secure your Web3 identity with a ZNS domain name. Replace long wallet addresses with your name.
          </ZNSDescription>
          <HeroCardButton onClick={(e) => {
            e.stopPropagation();
            setZnsModalOpen(true);
          }}>Register</HeroCardButton>
        </ZNSCard>
        
        {/* Create Token Card */}
        <CreateCard onClick={() => navigate('/create')}>
          <CreateIcon>
            <Plus />
          </CreateIcon>
          <HeroCardTitle>Create Token</HeroCardTitle>
          <HeroCardSubtitle style={{ marginBottom: '16px' }}>
            Launch your token on Ink in seconds
          </HeroCardSubtitle>
          <HeroCardButton>Get Started</HeroCardButton>
        </CreateCard>
      </HeroSection>
      
      {/* ============ TOKEN LIST SECTIONS ============ */}
      <ListSectionContainer>
        {/* Recently Deployed */}
        <ListSection>
          <ListHeader>
            <ListTitle>
              <Clock /> Recently Deployed
            </ListTitle>
            <ListExpandButton onClick={() => setRecentlyDeployedExpanded(!recentlyDeployedExpanded)}>
              <ChevronRight style={{ transform: recentlyDeployedExpanded ? 'rotate(90deg)' : 'none' }} />
            </ListExpandButton>
          </ListHeader>
          <ListContent $expanded={recentlyDeployedExpanded}>
            {displayedRecentlyDeployed.length > 0 ? (
              displayedRecentlyDeployed.map((token, index) => {
                const priceUSD = token.contract_address ? getPriceForToken(token.contract_address, priceChainId) : null;
                const change24h = token.contract_address ? get24hChangeForToken(token.contract_address, priceChainId) : null;
                const isFavorited = localFavorites.has(token.id) || token.is_favorited;
                
                return (
                  <TokenRow key={token.id || index} onClick={() => navigate(`/token/${token.contract_address}`)}>
                    <TokenLogo 
                      src={ensureHttpsUrl(token.logo_url)} 
                      alt={token.name}
                      onError={(e) => { (e.target as HTMLImageElement).src = '/token-placeholder.png'; }}
                    />
                    <TokenInfo>
                      <TokenNameRow>
                        <TokenName>{token.name}</TokenName>
                        <TokenDexLogo src={getNetworkLogo(token.dex)} alt={token.dex || 'DEX'} />
                      </TokenNameRow>
                      <TokenSymbol>${token.symbol}</TokenSymbol>
                    </TokenInfo>
                    <TokenStats>
                      <TokenMarketCap>
                        {priceUSD ? formatMarketCap(priceUSD) : '--'}
                      </TokenMarketCap>
                      <TokenPrice>
                        {formatPriceDisplay(priceUSD)}
                      </TokenPrice>
                      {change24h !== null && (
                        <PriceChange $positive={change24h >= 0}>
                          {formatPriceChange(change24h)}
                        </PriceChange>
                      )}
                    </TokenStats>
                    <TokenActions>
                      <TokenTradeButton onClick={(e) => {
                        e.stopPropagation();
                        handleTokenClick(token);
                      }}>
                        Trade
                      </TokenTradeButton>
                      <TokenFavoriteButton 
                        $isFavorited={isFavorited}
                        onClick={(e) => handleToggleFavorite(token.id, e)}
                        title={isFavorited ? "Remove from favorites" : "Add to favorites"}
                      >
                        <Star />
                      </TokenFavoriteButton>
                    </TokenActions>
                  </TokenRow>
                );
              })
            ) : (
              <EmptyState>No tokens found</EmptyState>
            )}
          </ListContent>
        </ListSection>
        
        {/* Featured */}
        <ListSection>
          <ListHeader>
            <ListTitle>
              <Sparkles /> Featured
            </ListTitle>
            <ListExpandButton onClick={() => setFeaturedExpanded(!featuredExpanded)}>
              <ChevronRight style={{ transform: featuredExpanded ? 'rotate(90deg)' : 'none' }} />
            </ListExpandButton>
          </ListHeader>
          <ListContent $expanded={featuredExpanded}>
            {displayedFeatured.length > 0 ? (
              displayedFeatured.map((token, index) => {
                const priceUSD = token.contract_address ? getPriceForToken(token.contract_address, priceChainId) : null;
                const change24h = token.contract_address ? get24hChangeForToken(token.contract_address, priceChainId) : null;
                const isFavorited = localFavorites.has(token.id) || token.is_favorited;
                
                return (
                  <TokenRow key={token.id || index} onClick={() => navigate(`/token/${token.contract_address}`)}>
                    <TokenLogo 
                      src={ensureHttpsUrl(token.logo_url)} 
                      alt={token.name}
                      onError={(e) => { (e.target as HTMLImageElement).src = '/token-placeholder.png'; }}
                    />
                    <TokenInfo>
                      <TokenNameRow>
                        <TokenName>{token.name}</TokenName>
                        <TokenDexLogo src={getNetworkLogo(token.dex)} alt={token.dex || 'DEX'} />
                      </TokenNameRow>
                      <TokenSymbol>${token.symbol}</TokenSymbol>
                    </TokenInfo>
                    <TokenStats>
                      <TokenMarketCap>
                        {priceUSD ? formatMarketCap(priceUSD) : '--'}
                      </TokenMarketCap>
                      <TokenPrice>
                        {formatPriceDisplay(priceUSD)}
                      </TokenPrice>
                      {change24h !== null && (
                        <PriceChange $positive={change24h >= 0}>
                          {formatPriceChange(change24h)}
                        </PriceChange>
                      )}
                    </TokenStats>
                    <TokenActions>
                      <TokenTradeButton onClick={(e) => {
                        e.stopPropagation();
                        handleTokenClick(token);
                      }}>
                        Trade
                      </TokenTradeButton>
                      <TokenFavoriteButton 
                        $isFavorited={isFavorited}
                        onClick={(e) => handleToggleFavorite(token.id, e)}
                        title={isFavorited ? "Remove from favorites" : "Add to favorites"}
                      >
                        <Star />
                      </TokenFavoriteButton>
                    </TokenActions>
                  </TokenRow>
                );
              })
            ) : (
              <EmptyState>No featured tokens</EmptyState>
            )}
          </ListContent>
        </ListSection>
        
        {/* Community Favorites */}
        <ListSection>
          <ListHeader>
            <ListTitle>
              <Star /> Community Favorites
            </ListTitle>
            <ListExpandButton onClick={() => setFavoritesExpanded(!favoritesExpanded)}>
              <ChevronRight style={{ transform: favoritesExpanded ? 'rotate(90deg)' : 'none' }} />
            </ListExpandButton>
          </ListHeader>
          <ListContent $expanded={favoritesExpanded}>
            {displayedFavorites.length > 0 ? (
              displayedFavorites.map((token: any, index: number) => {
                const priceUSD = token.contract_address ? getPriceForToken(token.contract_address, priceChainId) : null;
                const change24h = token.contract_address ? get24hChangeForToken(token.contract_address, priceChainId) : null;
                const isFavorited = localFavorites.has(token.id) || token.is_favorited || true; // Already favorited if in this list
                
                return (
                  <TokenRow key={token.id || index} onClick={() => navigate(`/token/${token.contract_address}`)}>
                    <TokenLogo 
                      src={ensureHttpsUrl(token.logo_url)} 
                      alt={token.name}
                      onError={(e) => { (e.target as HTMLImageElement).src = '/token-placeholder.png'; }}
                    />
                    <TokenInfo>
                      <TokenNameRow>
                        <TokenName>{token.name}</TokenName>
                        <TokenDexLogo src={getNetworkLogo(token.dex)} alt={token.dex || 'DEX'} />
                      </TokenNameRow>
                      <TokenSymbol>${token.symbol}</TokenSymbol>
                    </TokenInfo>
                    <TokenStats>
                      <TokenMarketCap>
                        {priceUSD ? formatMarketCap(priceUSD) : '--'}
                      </TokenMarketCap>
                      {change24h !== null ? (
                        <PriceChange $positive={change24h >= 0}>
                          {formatPriceChange(change24h)}
                        </PriceChange>
                      ) : (
                        <TokenFavorites>
                          <Star /> {token.favorites_count || 0}
                        </TokenFavorites>
                      )}
                    </TokenStats>
                    <TokenActions>
                      <TokenTradeButton onClick={(e) => {
                        e.stopPropagation();
                        handleTokenClick(token);
                      }}>
                        Trade
                      </TokenTradeButton>
                      <TokenFavoriteButton 
                        $isFavorited={isFavorited}
                        onClick={(e) => handleToggleFavorite(token.id, e)}
                        title={isFavorited ? "Remove from favorites" : "Add to favorites"}
                      >
                        <Star />
                      </TokenFavoriteButton>
                    </TokenActions>
                  </TokenRow>
                );
              })
            ) : (
              <EmptyState>No favorites yet</EmptyState>
            )}
          </ListContent>
        </ListSection>
      </ListSectionContainer>
      
      {/* ============ SCROLLING TOKEN FEED ============ */}
      {scrollingTokens.length > 0 && (
        <ScrollingFeedContainer>
          <ScrollingFeedTrack>
            {/* Duplicate the tokens to create seamless loop */}
            {[...scrollingTokens, ...scrollingTokens]
              .filter(token => !brokenImages.has(token.id))
              .map((token, index) => {
                const priceUSD = token.contract_address ? getPriceForToken(token.contract_address, priceChainId) : null;
                const change24h = token.contract_address ? get24hChangeForToken(token.contract_address, priceChainId) : null;
                
                return (
                  <ScrollingTokenCard 
                    key={`${token.id}-${index}`}
                    onClick={() => handleTokenClick(token)}
                  >
                    <ScrollingTokenLogo 
                      src={ensureHttpsUrl(token.logo_url)} 
                      alt={token.symbol}
                      onError={(e) => { 
                        // Hide this token from the feed
                        setBrokenImages(prev => new Set([...prev, token.id]));
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <ScrollingTokenInfo>
                      <ScrollingTokenSymbol>${token.symbol}</ScrollingTokenSymbol>
                      <ScrollingTokenCategory>{token.category || 'Token'}</ScrollingTokenCategory>
                    </ScrollingTokenInfo>
                    <ScrollingTokenMarketCap>
                      {priceUSD ? formatMarketCap(priceUSD) : '--'}
                    </ScrollingTokenMarketCap>
                    {change24h !== null && (
                      <PriceChange $positive={change24h >= 0} style={{ fontSize: '11px' }}>
                        {formatPriceChange(change24h)}
                      </PriceChange>
                    )}
                  </ScrollingTokenCard>
                );
              })}
          </ScrollingFeedTrack>
        </ScrollingFeedContainer>
      )}
      
      {/* ============ MODALS ============ */}
      <ZNSRegistrationModal 
        isVisible={znsModalOpen} 
        onClose={() => setZnsModalOpen(false)} 
      />
      
      <TradeModal
        isOpen={tradeModalOpen}
        onClose={() => {
          setTradeModalOpen(false);
          setSelectedToken(null);
        }}
        token={selectedToken}
      />
      
      {/* Solana Trade Modal - wrapped in SolanaWalletProvider for Orca direct swaps */}
      <SolanaWalletProvider>
        <SolanaTradeModal
          isOpen={solanaTradeModalOpen}
          onClose={() => {
            setSolanaTradeModalOpen(false);
            setSelectedToken(null);
          }}
          token={selectedToken}
        />
      </SolanaWalletProvider>
    </PageContainer>
  );
};

export default ExplorePageV2;

